/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/federation.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


/**
 * `refuse` is the default and the only one that enforces anything. `report_only` records and
 * permits, for a tenant mid-migration who needs to see what would break before turning it on.
 * There is deliberately no `warn`-that-proceeds: a residency breach is a legal exposure, and
 * letting work continue while logging a warning is how a control becomes decorative.
 */
export type Enforcement = 'refuse' | 'report_only';

export const ENFORCEMENTS: readonly Enforcement[] = ['refuse', 'report_only'];

export function isEnforcement(value: unknown): value is Enforcement {
  return (['refuse', 'report_only'] as readonly unknown[]).includes(value);
}

/**
 * Four answers, not two. `unrestricted` means no policy governs this tenant — permitted, and a
 * different fact from `permitted`, so "we checked and it was allowed" and "there was nothing to
 * check" stay apart. `unplaced` and `out_of_region` are both refusals and different problems: one
 * is configuration missing, the other configuration violated, and collapsing them would send an
 * engineer looking for a policy breach when nobody has declared where the database is.
 */
export type ResidencyVerdict = 'permitted' | 'unrestricted' | 'unplaced' | 'out_of_region';

export const RESIDENCY_VERDICTS: readonly ResidencyVerdict[] = ['permitted', 'unrestricted', 'unplaced', 'out_of_region'];

export function isResidencyVerdict(value: unknown): value is ResidencyVerdict {
  return (['permitted', 'unrestricted', 'unplaced', 'out_of_region'] as readonly unknown[]).includes(value);
}

/**
 * `unreachable` is **not zero**: a region reporting nothing and a region that could not be asked
 * are opposite facts, and a zero would silently shrink the estate.
 */
export type PlaneStatus = 'answered' | 'unreachable' | 'not_configured';

export const PLANE_STATUSES: readonly PlaneStatus[] = ['answered', 'unreachable', 'not_configured'];

export function isPlaneStatus(value: unknown): value is PlaneStatus {
  return (['answered', 'unreachable', 'not_configured'] as readonly unknown[]).includes(value);
}

export interface Region {
  readonly code: string;
  readonly display_name: string;
  /** ISO 3166-1 alpha-2. The legal fact behind the region, and what a regulator asks about. */
  readonly jurisdiction: string;
  /** Whether this process serves the region. Exactly one region is local per deployment. */
  readonly is_local: boolean;
}

export function isRegion(value: unknown): value is Region {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['code'] === 'string' &&
    typeof candidate['display_name'] === 'string' &&
    typeof candidate['jurisdiction'] === 'string' &&
    typeof candidate['is_local'] === 'boolean'
  );
}

export interface ResidencyPolicy {
  /**
   * False means no policy exists, so every region is permitted. Reported as its own field rather
   * than as an empty `permitted_regions`, which would read like a configured policy permitting
   * nothing — the opposite meaning.
   */
  readonly declared: boolean;
  readonly permitted_regions: readonly string[];
  readonly enforcement: Enforcement | null;
  readonly updated_by?: string;
  readonly updated_at?: string;
  /** Present only when undeclared, explaining what that means. */
  readonly note?: string;
}

export function isResidencyPolicy(value: unknown): value is ResidencyPolicy {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['declared'] === 'boolean' &&
    Array.isArray(candidate['permitted_regions']) &&
    (candidate['permitted_regions'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    (candidate['enforcement'] === null || isEnforcement(candidate['enforcement']))
  );
}

export interface Placement {
  readonly connection_id: string;
  readonly region_code: string;
  readonly declared_by: string;
  readonly declared_at: string;
}

export function isPlacement(value: unknown): value is Placement {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['connection_id'] === 'string' &&
    typeof candidate['region_code'] === 'string' &&
    typeof candidate['declared_by'] === 'string' &&
    typeof candidate['declared_at'] === 'string'
  );
}

export interface ResidencyDecision {
  readonly connection_id: string;
  readonly verdict: ResidencyVerdict;
  readonly region_code: string | null;
  readonly permitted_regions: readonly string[];
  readonly enforcement: Enforcement | null;
  /**
   * Whether work may proceed. A `report_only` breach is allowed and still reports breached as
   * true.
   */
  readonly allowed: boolean;
  readonly breached: boolean;
}

export function isResidencyDecision(value: unknown): value is ResidencyDecision {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['connection_id'] === 'string' &&
    (['permitted', 'unrestricted', 'unplaced', 'out_of_region'] as readonly unknown[]).includes(candidate['verdict']) &&
    (typeof candidate['region_code'] === 'string' || candidate['region_code'] === null) &&
    Array.isArray(candidate['permitted_regions']) &&
    (candidate['permitted_regions'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    (candidate['enforcement'] === null || isEnforcement(candidate['enforcement'])) &&
    typeof candidate['allowed'] === 'boolean' &&
    typeof candidate['breached'] === 'boolean'
  );
}

/**
 * **Five values and nothing else.** There is no field here a dataset name, a column name or a row
 * would fit in; adding one is the single change that would turn federated reporting into
 * cross-border data movement.
 */
export interface RegionalAggregate {
  readonly datasets: number;
  readonly scored_datasets: number;
  /**
   * Null when the region governs datasets and has scored none. Not zero — a zero from an unscored
   * region would drag the federated mean down as though it were the worst in the estate.
   */
  readonly mean_score: number | null;
  readonly as_of: string;
}

export function isRegionalAggregate(value: unknown): value is RegionalAggregate {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['datasets'] === 'number' &&
    typeof candidate['scored_datasets'] === 'number' &&
    (typeof candidate['mean_score'] === 'number' || candidate['mean_score'] === null) &&
    typeof candidate['as_of'] === 'string'
  );
}

export interface PlaneReport {
  readonly region_code: string;
  readonly status: PlaneStatus;
  readonly aggregate: RegionalAggregate | null;
  /**
   * Why a plane did not answer. Never the underlying error text, which can carry a host and a
   * port.
   */
  readonly detail: string;
}

export function isPlaneReport(value: unknown): value is PlaneReport {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['region_code'] === 'string' &&
    (['answered', 'unreachable', 'not_configured'] as readonly unknown[]).includes(candidate['status']) &&
    (candidate['aggregate'] === null || isRegionalAggregate(candidate['aggregate'])) &&
    typeof candidate['detail'] === 'string'
  );
}

export interface FederatedView {
  readonly generated_at: string;
  /** Whether every known region answered. **Read this before the totals.** */
  readonly complete: boolean;
  readonly silent_regions: readonly string[];
  readonly datasets: number;
  readonly scored_datasets: number;
  /**
   * Weighted by each region's scored count, never the mean of the regional means — that would make
   * a region with four datasets count as much as one with four thousand.
   */
  readonly mean_score: number | null;
  readonly regions: readonly PlaneReport[];
}

export function isFederatedView(value: unknown): value is FederatedView {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['generated_at'] === 'string' &&
    typeof candidate['complete'] === 'boolean' &&
    Array.isArray(candidate['silent_regions']) &&
    (candidate['silent_regions'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    typeof candidate['datasets'] === 'number' &&
    typeof candidate['scored_datasets'] === 'number' &&
    (typeof candidate['mean_score'] === 'number' || candidate['mean_score'] === null) &&
    Array.isArray(candidate['regions']) &&
    (candidate['regions'] as readonly unknown[]).every((item) => isPlaneReport(item))
  );
}
